import React, { useState, useEffect } from 'react';
import axios from 'axios';
import EntityMerge from '../components/EntityMerge';
import GroupMembership from '../components/GroupMembership';
import CollapsibleSection from '../components/CollapsibleSection';
import AgentEngagement from '../components/AgentEngagement';

const SECTIONS = [
  { id: 'pipeline', label: 'Pipeline' },
  { id: 'engagement', label: 'Agent Engagement' },
  { id: 'entities', label: 'Entities' },
];

function DachidoAdmin({ currentUser }) {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [overview, setOverview] = useState(null);
  const [transcription, setTranscription] = useState(null);
  const [entityExtraction, setEntityExtraction] = useState(null);
  const [dataQuality, setDataQuality] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Sub-tabs rather than one long scroll. Agent engagement answers a different question
  // from pipeline health and is read by a different person, so it gets its own page
  // instead of being appended below the pipeline metrics (issue #240 review).
  const [activeSection, setActiveSection] = useState('pipeline');

  // Fetch organizations (only those with a database)
  useEffect(() => {
    axios.get('/api/config/organizations')
      .then(res => {
        const orgs = (res.data.data || []).filter(o => o.type !== 'admin');
        setOrganizations(orgs);
        if (orgs.length && !selectedOrg) setSelectedOrg(orgs[0].name);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = () => {
    if (!selectedOrg) return;
    setLoading(true);
    setError(null);
    // Pass org name as pipeline param (backend resolves to DB via _pipeline_db)
    const params = { pipeline: selectedOrg };
    Promise.all([
      axios.get('/api/dachido/overview', { params }),
      axios.get('/api/dachido/transcription', { params }),
      axios.get('/api/dachido/entity-extraction', { params }),
      axios.get('/api/dachido/data-quality'),
      axios.get('/api/dachido/config'),
    ])
      .then(([o, t, e, dq, c]) => {
        setOverview(o.data);
        setTranscription(t.data);
        setEntityExtraction(e.data);
        setDataQuality(dq.data);
        setConfig(c.data);
      })
      .catch(err => setError(err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (selectedOrg) fetchAll(); }, [selectedOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  const metricCard = (label, value, sub) => (
    <div key={label} style={{ padding: '16px', background: '#fff', borderRadius: '8px', border: '1px solid #e9ecef', minWidth: '140px' }}>
      <div style={{ fontSize: '12px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 700, color: '#1a1a1a' }}>{value ?? '–'}</div>
      {sub != null && sub !== '' && <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>{sub}</div>}
    </div>
  );

  const dq = dataQuality?.available && dataQuality.data ? dataQuality.data : null;
  const errorCount = dq && typeof dq.errors === 'number' ? dq.errors : overview?.data_quality?.errors;

  return (
    <div style={{ padding: '24px' }}>
      <div className="module-header" style={{ marginBottom: '24px' }}>
        <h2>Org Admin</h2>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', marginBottom: '24px' }}>
        <label style={{ fontWeight: 600, color: '#495057' }}>Organization:</label>
        <select
          value={selectedOrg}
          onChange={e => setSelectedOrg(e.target.value)}
          style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '6px', border: '1px solid #dee2e6', minWidth: '160px' }}
        >
          {organizations.map(o => <option key={o.name} value={o.name}>{o.display_name}</option>)}
        </select>
        <button
          type="button"
          onClick={fetchAll}
          disabled={loading}
          style={{ padding: '10px 20px', fontSize: '14px', fontWeight: 600, color: '#fff', background: '#1e3a5f', border: 'none', borderRadius: '8px', cursor: loading ? 'wait' : 'pointer' }}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && <p style={{ color: '#dc3545', marginBottom: '16px' }}>{error}</p>}

      {/* Section tabs — same idiom as FieldHQ's Admin HQ */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px',
                    borderBottom: '1px solid #e9ecef', flexWrap: 'wrap' }}>
        {SECTIONS.map(sec => (
          <button key={sec.id} type="button" onClick={() => setActiveSection(sec.id)}
            style={{
              padding: '10px 18px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
              border: 'none', background: 'transparent',
              color: activeSection === sec.id ? '#1e3a5f' : '#6c757d',
              borderBottom: activeSection === sec.id ? '2px solid #1e3a5f' : '2px solid transparent',
            }}>
            {sec.label}
          </button>
        ))}
      </div>

      {activeSection === 'pipeline' && (<>

      {/* Per-org metrics (a–g) for selected organization */}
      {selectedOrg && (
        <section style={{ marginBottom: '32px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #dee2e6' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '18px', color: '#1a1a1a' }}>Metrics for: {organizations.find(o => o.name === selectedOrg)?.display_name || selectedOrg}</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            {overview && metricCard('a. Processed transcriptions', overview.transcriptions_total)}
            {entityExtraction && metricCard('c. Extracted entities (total)', entityExtraction.entity_mentions_total)}
            {entityExtraction && metricCard('d. Unrecognized entities', entityExtraction.unrecognized_count, entityExtraction.unrecognized_pending != null ? `${entityExtraction.unrecognized_pending} pending review` : null)}
            {overview && metricCard('e. Errors (latest run)', errorCount ?? '–')}
            {overview && metricCard('f. LLM calls', overview.llm_calls_total)}
          </div>
          {overview?.last_run && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#fff', borderRadius: '8px', fontSize: '13px', border: '1px solid #e9ecef' }}>
              <strong>b. Time measurements (last run):</strong> total_pipeline_seconds: {overview.last_run.total_pipeline_seconds ?? '–'}, outcome_extraction_seconds: {overview.last_run.outcome_extraction_seconds ?? '–'}, entity_extraction_seconds: {overview.last_run.entity_extraction_seconds ?? '–'} — run_at: {overview.last_run.run_at || overview.last_run.run_at_utc || '–'}
            </div>
          )}
        </section>
      )}

      {/* 1. Transcription & Translation Pipeline (Coromandel-style, including errors) */}
      <section style={{ marginBottom: '32px' }}>
        <h3 style={{ marginBottom: '16px', fontSize: '18px', color: '#1a1a1a' }}>1. Transcription & Translation Pipeline</h3>
        <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '12px' }}>Similar to Coromandel setup: status, performance metrics, and error reporting.</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          {transcription && metricCard('Transcriptions processed', transcription.transcriptions_processed)}
          {metricCard('Errors (latest run)', errorCount ?? '–')}
        </div>
        {transcription?.run_history && (
          <div style={{ marginTop: '16px', padding: '12px', background: '#f8f9fa', borderRadius: '8px', fontSize: '13px' }}>
            Run history: from transcription_runs.json (when LOG_DIR is set).
          </div>
        )}
      </section>

      {/* 2. Entity Extraction Pipeline (status depends on pipeline steps) */}
      <section style={{ marginBottom: '32px' }}>
        <h3 style={{ marginBottom: '16px', fontSize: '18px', color: '#1a1a1a' }}>2. Entity Extraction Pipeline</h3>
        <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '12px' }}>Status depends on the pipeline (number of steps, execution flow, etc.). Different pipelines (DrRoof, Coromandel) may have different steps.</p>
        {entityExtraction && (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
              {metricCard('Entity mentions total', entityExtraction.entity_mentions_total)}
              {metricCard('LLM calls', entityExtraction.llm_calls)}
              {metricCard('Unrecognized', entityExtraction.unrecognized_count, entityExtraction.unrecognized_pending != null ? `${entityExtraction.unrecognized_pending} pending` : null)}
            </div>
            {entityExtraction.entities_by_type?.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <strong>c. Extracted entities — breakdown by type:</strong>
                <table style={{ width: '100%', maxWidth: '400px', marginTop: '8px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px' }}>Type</th>
                      <th style={{ padding: '8px', textAlign: 'right', fontSize: '12px' }}>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entityExtraction.entities_by_type.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px' }}>{row.entity_type ?? '–'}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{row.count ?? row.c ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(entityExtraction.pipeline_runs || entityExtraction.entity_extraction_runs) && (
              <div style={{ marginTop: '12px', padding: '12px', background: '#f8f9fa', borderRadius: '8px', fontSize: '13px' }}>
                <strong>Last run / steps:</strong> {Array.isArray(entityExtraction.pipeline_runs) && entityExtraction.pipeline_runs.length
                  ? JSON.stringify(entityExtraction.pipeline_runs.slice(-1)[0])
                  : Array.isArray(entityExtraction.entity_extraction_runs) && entityExtraction.entity_extraction_runs.length
                    ? JSON.stringify(entityExtraction.entity_extraction_runs.slice(-1)[0])
                    : 'N/A (set LOG_DIR for file-based runs)'}
              </div>
            )}
          </>
        )}
      </section>

      {/* g. Other: latest run stats & errors */}
      {dq && (
        <section style={{ marginBottom: '32px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '18px', color: '#1a1a1a' }}>g. Latest run stats & other metrics</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            {Object.entries(dq)
              .filter(([, v]) => typeof v === 'number' || typeof v === 'string')
              .map(([k, v]) => metricCard(k, v))}
          </div>
        </section>
      )}

      {/* Configuration */}
      {config && (
        <section style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '18px', color: '#1a1a1a' }}>Configuration (safe)</h3>
          <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', fontSize: '13px', fontFamily: 'monospace' }}>
            {Object.entries(config).map(([k, v]) => v != null && <div key={k}>{k}: {String(v)}</div>)}
          </div>
        </section>
      )}
      </>)}

      {/* Agent engagement — the weekly numbers collected by hand today (#240).
          Its own page: a different question from pipeline health, and a different reader. */}
      {activeSection === 'engagement' && selectedOrg && <AgentEngagement org={selectedOrg} />}

      {/* Entity curation. Both tools are tall and neither is used on most visits, so they
          open on demand — and stay unmounted until then, so a closed one fetches nothing. */}
      {activeSection === 'entities' && selectedOrg && (<>
        <CollapsibleSection
          title="Merge duplicate entities"
          subtitle="Collapse spelling variants of the same thing into one entity.">
          <EntityMerge org={selectedOrg} currentUser={currentUser} />
        </CollapsibleSection>
        <CollapsibleSection
          title="Group membership"
          subtitle="Which entities sit in which curated category.">
          <GroupMembership org={selectedOrg} currentUser={currentUser} />
        </CollapsibleSection>
      </>)}

    </div>
  );
}

export default DachidoAdmin;
